import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import L from "leaflet";
import { newBaseMap, projectFor, type BaseProvider } from "@/lib/amap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  generateInitialVisitorGPS,
  driftGPS,
  scenicSpotsList,
  type VisitorGPSEntry,
} from "@/mocks/dashboard";
import { useApi } from "@/hooks/useApi";
import { apiGet } from "@/lib/api";

type TimeRange = "today" | "7d" | "30d";

interface DashboardStat {
  title: string;
  value: number;
  change: number;
  changeLabel: string;
}

interface DeviceStatus {
  name: string;
  value: number;
}

interface DashboardActivity {
  type: "question" | "faceswap" | "device";
  content: string;
  timeLabel: string;
}

function StatCard({ title, value, change, changeLabel, icon, iconColor, iconBg, isLive, onClick }: {
  title: string;
  value: number;
  change: number;
  changeLabel: string;
  icon: string;
  iconColor: string;
  iconBg: string;
  isLive?: boolean;
  onClick?: () => void;
}) {
  const [displayValue, setDisplayValue] = useState(0);
  const targetValue = value;
  const prevValueRef = useRef(value);
  const isPositive = change >= 0;

  // Single cancellable count-up. Without the cleanup, the initial (mock
  // fallback) animation and the real-data animation raced rAF loops and
  // the longer one won, freezing cards on stale numbers.
  useEffect(() => {
    const startValue = prevValueRef.current === targetValue ? 0 : prevValueRef.current;
    const duration = startValue === 0 ? 800 : 400;
    const startTime = Date.now();
    let raf = 0;
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, startValue === 0 ? 4 : 3);
      setDisplayValue(Math.round(startValue + (targetValue - startValue) * eased));
      if (progress < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    prevValueRef.current = targetValue;
    return () => cancelAnimationFrame(raf);
  }, [targetValue]);

  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-lg p-5 border border-gray-200 hover:-translate-y-0.5 hover:shadow-md transition-all duration-300 ${onClick ? "cursor-pointer" : ""}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 font-medium">{title}</span>
          {isLive && (
            <span className="flex items-center gap-1 text-xs text-teal-600 font-medium">
              <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
              LIVE
            </span>
          )}
        </div>
        <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${iconBg}`}>
          <i className={`${icon} ${iconColor}`}></i>
        </div>
      </div>
      <div className="text-2xl font-bold text-gray-900 mb-1">
        {displayValue.toLocaleString()}
      </div>
      <div className="flex items-center gap-1">
        <i
          className={`${isPositive ? "ri-arrow-up-line" : "ri-arrow-down-line"} text-xs ${
            isPositive ? "text-emerald-600" : "text-red-500"
          }`}
        ></i>
        <span className={`text-xs font-medium ${isPositive ? "text-emerald-600" : "text-red-500"}`}>
          {Math.abs(change)}%
        </span>
        <span className="text-xs text-gray-400">{changeLabel}</span>
      </div>
    </div>
  );
}

function HeatmapChart() {
  const [timeRange, setTimeRange] = useState<TimeRange>("today");
  // 真实埋点:按时段从 /dashboard/heatmap 拉景点讲解触发次数(替代原 mock)。
  const [data, setData] = useState<{ name: string; triggers: number }[]>([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let alive = true;
    setLoaded(false);
    apiGet<{ name: string; triggers: number }[]>(`/dashboard/heatmap?range=${timeRange}`)
      .then((d) => { if (alive) setData(Array.isArray(d) ? d : []); })
      .catch(() => { if (alive) setData([]); })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [timeRange]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900">航线讲解触发热力</h3>
        <div className="flex items-center bg-gray-100 rounded-lg p-1">
          {(["today", "7d", "30d"] as TimeRange[]).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                timeRange === range
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {range === "today" ? "今日" : range === "7d" ? "7 天" : "30 天"}
            </button>
          ))}
        </div>
      </div>
      <div className="h-72">
        {loaded && data.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-gray-400">
            <i className="ri-bar-chart-2-line text-3xl mb-2" />
            <p className="text-sm">该时段暂无讲解触发数据</p>
            <p className="text-xs mt-0.5">游船运营产生讲解触发后自动统计</p>
          </div>
        ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: "#6b7280" }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: "#6b7280" }}
            />
            <Tooltip
              contentStyle={{
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                fontSize: "12px",
              }}
            />
            <Bar dataKey="triggers" fill="#0d9488" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

function DeviceStatusChart({ liveData }: { liveData: { online: number; offline: number; lowBattery: number; maintenance: number } }) {
  const chartData = [
    { name: "在线", value: liveData.online, color: "#10b981" },
    { name: "离线", value: liveData.offline, color: "#9ca3af" },
    { name: "低电", value: liveData.lowBattery, color: "#ef4444" },
    { name: "维护中", value: liveData.maintenance, color: "#8b5cf6" },
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <h3 className="text-base font-semibold text-gray-900 mb-4">设备状态分布</h3>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={90}
              paddingAngle={2}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                fontSize: "12px",
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        {chartData.map((item) => (
          <div key={item.name} className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }}></span>
            <span className="text-xs text-gray-600">
              {item.name} ({item.value})
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

interface LiveActivity {
  id: string;
  type: "pair" | "question" | "faceswap" | "report" | "device";
  content: string;
  timeLabel: string;
  isNew?: boolean;
}

function ActivityFeed({ liveActivities, bare }: { liveActivities: LiveActivity[]; bare?: boolean }) {
  const getIconByType = (type: string) => {
    switch (type) {
      case "pair":
        return { icon: "ri-link", color: "text-teal-600", bg: "bg-teal-50" };
      case "question":
        return { icon: "ri-chat-3-line", color: "text-amber-600", bg: "bg-amber-50" };
      case "faceswap":
        return { icon: "ri-image-line", color: "text-rose-600", bg: "bg-rose-50" };
      case "report":
        return { icon: "ri-file-paper-line", color: "text-purple-600", bg: "bg-purple-50" };
      case "device":
        return { icon: "ri-alert-line", color: "text-red-600", bg: "bg-red-50" };
      default:
        return { icon: "ri-circle-line", color: "text-gray-600", bg: "bg-gray-50" };
    }
  };

  return (
    <div className={bare ? "flex flex-col h-full min-h-0 p-4" : "bg-white rounded-lg border border-gray-200 p-5"}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900">实时活动流</h3>
        <span className="text-xs text-teal-600 font-medium flex items-center gap-1">
          <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
          实时更新
        </span>
      </div>
      <div className={`space-y-3 overflow-y-auto pr-1 ${bare ? "flex-1 min-h-0" : "max-h-80"}`}>
        {liveActivities.map((item) => {
          const style = getIconByType(item.type);
          return (
            <div
              key={item.id}
              className={`flex items-start gap-3 transition-all duration-500 ${
                item.isNew ? "animate-[fadeIn_0.5s_ease-out]" : ""
              }`}
            >
              <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 ${style.bg}`}>
                <i className={`${style.icon} ${style.color} text-xs`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-800 leading-snug">{item.content}</p>
                <span className="text-xs text-gray-400 mt-0.5 block">{item.timeLabel}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- 实时游客地图(高德底图) ---
interface AreaGroup {
  area: string;
  centerLat: number;
  centerLng: number;
  visitors: VisitorGPSEntry[];
}

function DashboardLiveMap({
  areaGroups,
  selectedArea,
  onToggleArea,
  trackingVisitor,
  trailReplayProgress,
  isReplaying,
}: {
  areaGroups: AreaGroup[];
  selectedArea: string | null;
  onToggleArea: (area: string) => void;
  trackingVisitor: VisitorGPSEntry | null;
  trailReplayProgress: number;
  isReplaying: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const spotsLayerRef = useRef<L.LayerGroup | null>(null);
  const trailLayerRef = useRef<L.LayerGroup | null>(null);
  const shipLayerRef = useRef<L.LayerGroup | null>(null);
  const lastTrackedIdRef = useRef<string | null>(null);
  const onToggleRef = useRef(onToggleArea);
  onToggleRef.current = onToggleArea;
  // 底图 provider：天地图为主，瓦片连续失败 → 整图回退高德（见 lib/amap newBaseMap）。
  const [provider, setProvider] = useState<BaseProvider>("tdt");
  // 投影：天地图原样 WGS-84；高德需转 GCJ-02。provider 变 → 各绘制 effect 重投影。
  const project = projectFor(provider);

  // Create the map, framed on the 两坝一峡 spots. Re-creates on provider flip
  // (天地图↔高德 use different datums and must not be mixed).
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = newBaseMap(containerRef.current, provider, () => setProvider("amap"));
    const pts = scenicSpotsList.map((s) => project(s.centerLat, s.centerLng));
    map.fitBounds(L.latLngBounds(pts), { padding: [36, 36] });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      // 整图重建：旧图层组随地图销毁，清引用让绘制 effect 在新图上从头重绘。
      spotsLayerRef.current = null;
      trailLayerRef.current = null;
      shipLayerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider]);

  // 游船亮点:以游船为单位一个闪动亮点。当前所有设备同一船 → 取在线游客质心作为船位;
  // 后续多船时按 visitor.ship 分组各画一个(数据加 ship 字段后即可扩展)。
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    shipLayerRef.current?.remove();
    const g = L.layerGroup().addTo(map);
    shipLayerRef.current = g;
    const online = areaGroups.flatMap((grp) => grp.visitors.filter((v) => v.online));
    if (!online.length) return;
    const lat = online.reduce((a, v) => a + v.lat, 0) / online.length;
    const lng = online.reduce((a, v) => a + v.lng, 0) / online.length;
    const [plat, plng] = project(lat, lng);
    L.marker([plat, plng], {
      icon: L.divIcon({ className: "", html: `<div class="ship-pulse"></div>`, iconAnchor: [11, 11] }),
      zIndexOffset: 1000,
      interactive: false,
    })
      .addTo(g)
      .bindTooltip("长江三峡1号", { permanent: true, direction: "top", offset: [0, -14], className: "ship-label" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, areaGroups]);

  // Per-spot online-count badges.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    spotsLayerRef.current?.remove();
    const g = L.layerGroup().addTo(map);
    spotsLayerRef.current = g;
    areaGroups.forEach((group) => {
      const online = group.visitors.filter((v) => v.online).length;
      if (online === 0) return;
      const [lat, lng] = project(group.centerLat, group.centerLng);
      const active = selectedArea === group.area;
      L.marker([lat, lng], {
        icon: L.divIcon({
          className: "",
          html: `<div class="dash-spot${active ? " dash-spot-active" : ""}"><span class="dash-spot-count">${online}</span><span class="dash-spot-name">${group.area}</span></div>`,
          iconAnchor: [13, 13],
        }),
      })
        .on("click", () => onToggleRef.current(group.area))
        .addTo(g);
    });
  }, [provider, areaGroups, selectedArea]);

  // Tracked visitor trail: full path dashed, progress solid, pulsing head.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    trailLayerRef.current?.remove();
    trailLayerRef.current = null;
    if (!trackingVisitor || trackingVisitor.trail.length < 2) {
      lastTrackedIdRef.current = null;
      return;
    }
    const g = L.layerGroup().addTo(map);
    trailLayerRef.current = g;
    const full = trackingVisitor.trail.map((p) => project(p.lat, p.lng));
    L.polyline(full, { color: "#9ca3af", weight: 2, dashArray: "4 3", opacity: 0.7 }).addTo(g);
    const shown = isReplaying ? full.slice(0, Math.max(trailReplayProgress, 1)) : full;
    if (shown.length >= 2) {
      L.polyline(shown, { color: "#0d9488", weight: 3, opacity: 0.9 }).addTo(g);
    }
    L.circleMarker(full[0], { radius: 4, color: "#0d9488", weight: 2, fillColor: "#ffffff", fillOpacity: 1 }).addTo(g);
    const head = shown[shown.length - 1];
    L.marker(head, {
      icon: L.divIcon({ className: "", html: `<div class="dash-trail-head"></div>`, iconAnchor: [7, 7] }),
      interactive: false,
    }).addTo(g);

    // Zoom to the trail when tracking starts; follow the head during replay.
    if (lastTrackedIdRef.current !== trackingVisitor.id) {
      lastTrackedIdRef.current = trackingVisitor.id;
      map.fitBounds(L.latLngBounds(full), { padding: [60, 60], maxZoom: 16 });
    } else if (isReplaying) {
      map.panTo(head, { animate: true });
    }
  }, [provider, trackingVisitor, trailReplayProgress, isReplaying]);

  return (
    <div className="lg:w-[60%] h-[320px] md:h-[420px] relative">
      <style>{`
        .dash-spot { position: relative; display: flex; flex-direction: column; align-items: center; cursor: pointer; }
        .dash-spot-count {
          display: flex; align-items: center; justify-content: center;
          width: 26px; height: 26px; border-radius: 9999px;
          background: #14b8a6; color: #fff; font-size: 11px; font-weight: 700;
          border: 2px solid #fff; box-shadow: 0 1px 4px rgba(0,0,0,.35);
        }
        .dash-spot-active .dash-spot-count { background: #0f766e; transform: scale(1.15); }
        .dash-spot::before {
          content: ""; position: absolute; top: -3px; left: 50%; margin-left: -16px;
          width: 32px; height: 32px; border-radius: 9999px;
          background: rgba(20,184,166,.35); animation: dashSpotPing 1.8s ease-out infinite;
        }
        .dash-spot-name {
          margin-top: 2px; font-size: 10px; font-weight: 600; color: #134e4a;
          background: rgba(255,255,255,.9); padding: 0 5px; border-radius: 4px;
          white-space: nowrap; box-shadow: 0 1px 2px rgba(0,0,0,.15);
        }
        @keyframes dashSpotPing { 0% { transform: scale(.6); opacity: .8; } 100% { transform: scale(1.6); opacity: 0; } }
        .dash-trail-head {
          width: 14px; height: 14px; border-radius: 9999px; background: #0d9488;
          border: 3px solid rgba(13,148,136,.3); background-clip: padding-box;
          animation: dashSpotPing 1.2s ease-out infinite reverse;
        }
      `}</style>
      <div ref={containerRef} className="absolute inset-0 z-0" />
      {trackingVisitor && (
        <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur rounded-lg px-3 py-2 border border-gray-200 shadow-sm z-10">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-0.5 bg-teal-600 rounded"></span>
              <span className="text-xs text-gray-600">历史轨迹</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-0.5 bg-gray-300 rounded"></span>
              <span className="text-xs text-gray-600">完整路径</span>
            </div>
            <span className="text-xs text-gray-400">{trackingVisitor.trail.length} 个轨迹点</span>
            {isReplaying && (
              <span className="text-xs text-teal-600 font-medium animate-pulse">
                回放中... {trailReplayProgress}/{trackingVisitor.trail.length}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// --- 实时游客地图组件 ---
function VisitorLocationMap({ visitors, initialSpot, liveActivities }: { visitors: VisitorGPSEntry[]; initialSpot?: string; liveActivities: LiveActivity[] }) {
  const [selectedArea, setSelectedArea] = useState<string | null>(initialSpot || null);
  const [trackingVisitorId, setTrackingVisitorId] = useState<string | null>(null);
  const [trailReplayProgress, setTrailReplayProgress] = useState(0);
  const [isReplaying, setIsReplaying] = useState(false);
  const replayTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const onlineCount = visitors.filter((v) => v.online).length;
  const offlineCount = visitors.length - onlineCount;

  const areaGroups = scenicSpotsList.map((spot) => ({
    ...spot,
    visitors: visitors.filter((v) => v.area === spot.area),
  }));

  const filteredVisitors = selectedArea
    ? visitors.filter((v) => v.area === selectedArea)
    : visitors;

  const trackingVisitor = trackingVisitorId
    ? visitors.find((v) => v.id === trackingVisitorId)
    : null;

  // 轨迹回放动画
  useEffect(() => {
    if (isReplaying && trackingVisitor && trackingVisitor.trail.length > 0) {
      replayTimerRef.current = setInterval(() => {
        setTrailReplayProgress((prev) => {
          const next = prev + 1;
          if (next >= trackingVisitor.trail.length) {
            setIsReplaying(false);
            return trackingVisitor.trail.length - 1;
          }
          return next;
        });
      }, 80);
    }
    return () => {
      if (replayTimerRef.current) clearInterval(replayTimerRef.current);
    };
  }, [isReplaying, trackingVisitor]);

  const handleStartReplay = (visitorId: string) => {
    if (trackingVisitorId === visitorId && isReplaying) {
      setIsReplaying(false);
      return;
    }
    setTrackingVisitorId(visitorId);
    setTrailReplayProgress(0);
    setIsReplaying(true);
  };

  const handleResetTrail = () => {
    setTrackingVisitorId(null);
    setTrailReplayProgress(0);
    setIsReplaying(false);
    if (replayTimerRef.current) clearInterval(replayTimerRef.current);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold text-gray-900">实时游客位置</h3>
          <span className="flex items-center gap-1 text-xs text-teal-600 font-medium">
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
            LIVE
          </span>
        </div>
        <div className="flex items-center gap-4">
          {trackingVisitorId && (
            <button
              onClick={handleResetTrail}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 whitespace-nowrap"
            >
              <i className="ri-close-circle-line"></i>
              清除轨迹
            </button>
          )}
          <span className="text-xs text-gray-500">
            在线 <strong className="text-emerald-600">{onlineCount}</strong> / 总计 <strong>{visitors.length}</strong>
          </span>
          {offlineCount > 0 && (
            <span className="text-xs text-red-500 flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-red-400 rounded-full"></span>
              {offlineCount} 离线
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* 左侧：高德底图 + 游客/轨迹图层 */}
        <DashboardLiveMap
          areaGroups={areaGroups}
          selectedArea={selectedArea}
          onToggleArea={(area) => setSelectedArea(selectedArea === area ? null : area)}
          trackingVisitor={trackingVisitor ?? null}
          trailReplayProgress={trailReplayProgress}
          isReplaying={isReplaying}
        />


        {/* 右侧：实时活动流（替换原游客列表）*/}
        <div className="lg:w-[40%] flex flex-col border-l border-gray-100 max-h-[520px] min-h-0">
          <ActivityFeed liveActivities={liveActivities} bare />
        </div>
      </div>
    </div>
  );
}

// Real-time activity templates for generating new events
const liveActivityTemplates = [
  { type: "pair" as const, templates: [
    "眼镜 #A{dev} 已配对，绑定游客 {visitor}",
    "眼镜 #A{dev} 解除绑定，游客 {visitor} 行程结束",
  ]},
  { type: "question" as const, templates: [
    "游客提问『{question}』，AI 已回答",
  ]},
  { type: "faceswap" as const, templates: [
    "换脸照已生成：游客 {visitor} - {template}",
  ]},
  { type: "device" as const, templates: [
    "眼镜 #A{dev} 电量低于 20%，已推送换电提醒",
    "眼镜 #A{dev} 信号弱，已切换备用通道",
    "眼镜 #A{dev} 离线超过 15 分钟，已标记异常",
  ]},
  { type: "report" as const, templates: [
    "行程报告已生成：船次 YC20260609-{ship}，共 {count} 份",
  ]},
];

const deviceNums = ["101", "102", "103", "104", "105", "106", "107", "108", "109", "110", "111", "112"];
const visitors = ["陈明远", "王建国", "李婷", "张伟", "刘芳", "周杰", "孙丽", "吴敏", "郑浩", "黄静", "谢雪", "何伟"];
const questions = [
  "三峡大坝多高", "葛洲坝什么时候建成的", "黄牛岩怎么形成的",
  "三游洞有什么历史典故", "屈原是哪个朝代的人", "西陵峡有多长",
  "南津关有什么特别之处", "石牌要塞在哪个位置",
];
const templates = ["鲟侠船长造型模板", "三峡大坝合影模板", "金梅导游主题模板", "西陵峡风光模板"];
const ships = ["A", "B", "C"];

function generateLiveActivity(): { type: "pair" | "question" | "faceswap" | "report" | "device"; content: string } {
  const category = liveActivityTemplates[Math.floor(Math.random() * liveActivityTemplates.length)];
  const tpl = category.templates[Math.floor(Math.random() * category.templates.length)];

  const fillData: Record<string, string> = {
    dev: deviceNums[Math.floor(Math.random() * deviceNums.length)],
    visitor: visitors[Math.floor(Math.random() * visitors.length)],
    question: questions[Math.floor(Math.random() * questions.length)],
    template: templates[Math.floor(Math.random() * templates.length)],
    ship: ships[Math.floor(Math.random() * ships.length)],
    count: String(Math.floor(Math.random() * 20) + 35),
  };

  const content = tpl.replace(/\{(\w+)\}/g, (_, key) => fillData[key] || "?");
  return { type: category.type, content };
}

// 最近生成的换脸/头像照片缩略图(真实数据 /faceswap/records),点击放大。
interface PhotoRecord { id: string; imageUrl: string; templateName: string; status: string; createdAt: string }
interface PhotoRecordsPage { items: PhotoRecord[]; total: number; page: number; limit: number }
function RecentPhotosStrip() {
  const [records, setRecords] = useState<PhotoRecord[]>([]);
  const [zoom, setZoom] = useState<PhotoRecord | null>(null);
  useEffect(() => {
    let alive = true;
    apiGet<PhotoRecordsPage>("/faceswap/records?page=1&limit=18&status=success")
      .then((data) => { if (alive) setRecords(data.items); })
      .catch(() => { /* keep empty */ });
    return () => { alive = false; };
  }, []);
  const photos = records;
  if (!photos.length) return null;
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-gray-900">最近生成的照片</h3>
        <span className="text-xs text-gray-400">最新 {photos.length} 张 · 点击放大</span>
      </div>
      {/* 跑马灯:照片复制两份无缝循环左移;hover 暂停方便点击 */}
      <div className="overflow-hidden">
        <div className="flex w-max photo-marquee">
          {[...photos, ...photos].map((p, i) => (
            <button
              key={`${p.id}-${i}`}
              onClick={() => setZoom(p)}
              title={`${p.templateName} · ${p.createdAt}`}
              className="flex-shrink-0 w-20 h-20 mr-2.5 rounded-lg overflow-hidden border border-gray-100 hover:ring-2 hover:ring-teal-400 transition-shadow"
            >
              <img src={p.imageUrl} alt={p.templateName} loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
      {zoom && (
        <div
          className="fixed inset-0 z-[60] bg-black/75 flex flex-col items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setZoom(null)}
        >
          <img src={zoom.imageUrl} alt={zoom.templateName} className="max-w-[90vw] max-h-[82vh] rounded-lg shadow-2xl" />
          <div className="mt-3 text-white/90 text-sm">{zoom.templateName} · {zoom.createdAt}</div>
        </div>
      )}
    </div>
  );
}

function DashboardPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSpot = searchParams.get("spot") || undefined;
  const [liveEnabled, setLiveEnabled] = useState(true);
  // Visitor GPS remains presentation-only until per-device GPS telemetry is
  // exposed by the dashboard API.
  const [visitorGPS, setVisitorGPS] = useState<VisitorGPSEntry[]>(() =>
    generateInitialVisitorGPS()
  );

  const { data: dash, reload: reloadDash } = useApi<{
    stats: DashboardStat[];
    deviceStatus: DeviceStatus[];
    activity: DashboardActivity[];
  }>("/dashboard", { stats: [], deviceStatus: [], activity: [] });
  const apiStats = dash.stats ?? [];
  const apiDeviceStatus = dash.deviceStatus ?? [];
  const apiActivities = dash.activity ?? [];
  const issuedStat = apiStats.find((stat) => stat.title.includes("发放"));
  const onlineStat = apiStats.find((stat) => stat.title.includes("在线"));
  const questionsStat = apiStats.find((stat) => stat.title.includes("问答"));
  const faceswapStat = apiStats.find((stat) => stat.title.includes("换脸"));
  const deviceLiveData = {
    online: apiDeviceStatus.find((status) => status.name === "在线")?.value ?? 0,
    offline: apiDeviceStatus.find((status) => status.name === "离线")?.value ?? 0,
    lowBattery: apiDeviceStatus.find((status) => status.name === "低电")?.value ?? 0,
    maintenance: apiDeviceStatus.find((status) => status.name === "维护中")?.value ?? 0,
  };
  const liveActivities: LiveActivity[] = apiActivities.map((activity, index) => ({
    id: `api-${index}`,
    ...activity,
    isNew: false,
  }));

  // Real data is refreshed every five seconds while live mode is enabled.
  useEffect(() => {
    if (!liveEnabled) return;
    const interval = setInterval(() => {
      reloadDash();
      setVisitorGPS((prev) => driftGPS(prev));
    }, 5000);
    return () => clearInterval(interval);
  }, [liveEnabled, reloadDash]);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">概览看板</h1>
          <p className="text-sm text-gray-500 mt-1">
            {new Date().toLocaleDateString("zh-CN", { year: "numeric", month: "long", day: "numeric" })} 今日运营状态一览
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">实时推流</span>
          <button
            onClick={() => setLiveEnabled(!liveEnabled)}
            className={`relative w-11 h-6 rounded-full transition-colors ${
              liveEnabled ? "bg-teal-500" : "bg-gray-300"
            }`}
          >
            <span
              className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                liveEnabled ? "left-[22px]" : "left-0.5"
              }`}
            ></span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <StatCard
          title="今日发放眼镜数"
          onClick={() => navigate("/rental")}
          value={issuedStat?.value ?? 0}
          change={issuedStat?.change ?? 0}
          changeLabel={issuedStat?.changeLabel ?? "较昨日"}
          icon="ri-glasses-line"
          iconColor="text-teal-600"
          iconBg="bg-teal-50"
          isLive
        />
        <StatCard
          title="在线设备数"
          onClick={() => navigate("/devices")}
          value={onlineStat?.value ?? 0}
          change={onlineStat?.change ?? 0}
          changeLabel={onlineStat?.changeLabel ?? "较昨日"}
          icon="ri-wifi-line"
          iconColor="text-emerald-600"
          iconBg="bg-emerald-50"
          isLive
        />
        <StatCard
          title="今日 AI 问答次数"
          onClick={() => navigate("/analytics")}
          value={questionsStat?.value ?? 0}
          change={questionsStat?.change ?? 0}
          changeLabel={questionsStat?.changeLabel ?? "较昨日"}
          icon="ri-chat-3-line"
          iconColor="text-amber-600"
          iconBg="bg-amber-50"
          isLive
        />
        <StatCard
          title="今日换脸生成数"
          onClick={() => navigate("/augc-history")}
          value={faceswapStat?.value ?? 0}
          change={faceswapStat?.change ?? 0}
          changeLabel={faceswapStat?.changeLabel ?? "较昨日"}
          icon="ri-image-line"
          iconColor="text-rose-600"
          iconBg="bg-rose-50"
          isLive
        />
      </div>

      {/* 最近生成的照片缩略图(点击放大) */}
      <RecentPhotosStrip />

      {/* Real-time Visitor Map（右侧为实时活动流）*/}
      <VisitorLocationMap visitors={visitorGPS} initialSpot={initialSpot} liveActivities={liveActivities} />

      {/* Middle Row: Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <HeatmapChart />
        </div>
        <div className="lg:col-span-1">
          <DeviceStatusChart liveData={deviceLiveData} />
        </div>
      </div>

    </div>
  );
}

export default DashboardPage;